import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Video, Plus, Trash2, Calendar, Clock, Users, Radio,
  Play, Monitor, BookOpen, X, Loader, CheckCircle,
  StopCircle, ExternalLink, RefreshCw, ClipboardList,
  Ban, UserX, ShieldOff, DoorOpen, Mail, BarChart2, MessageSquare, CheckCheck, Send, Film,
} from "lucide-react";
import {
  lmsGetLiveClasses, lmsSyncLiveClassStatus, lmsCreateLiveClass, lmsUpdateLiveClass,
  lmsDeleteLiveClass, lmsGetLiveClassJoinUrl, lmsGetLiveClassStatus,
  lmsGetLiveClassRecordings, lmsDownloadRecording, lmsRecordingStatus, lmsGetClassSubjects, lmsEndLiveClass,
  lmsRecordAttendance, lmsGetAttendance,
  lmsGetActiveSession, lmsRegisterSession, lmsEndSession,
  lmsBlockParticipant, lmsUnblockParticipant, lmsGetBlockedUsers, lmsKickParticipant,
  lmsRequestJoinWaitingRoom, lmsCheckWaitingStatus,
  lmsGetWaitingRoom, lmsApproveWaitingUser, lmsRejectWaitingUser,
  lmsRecordLeave,
  lmsGetStudents,
  lmsGetInvitations, lmsSendInvitations, lmsRevokeInvitation,
  lmsCreatePoll, lmsGetActivePoll, lmsVotePoll, lmsClosePoll,
  lmsSubmitQuestion, lmsGetQA, lmsGetPendingQA, lmsApproveQuestion, lmsMarkAnswered, lmsDeleteQAMessage,
  lmsGetBatches,
} from "../api/lmsApi";
import { fetchLmsSchoolClasses } from "../redux/lmsSlice";
import { useDispatch } from "react-redux";
import { BASE_URL } from "../../../configs/WebServiceUrl";
import "../lms.css";

/* ── helpers ── */
const STATUS_META = {
  scheduled: { label: "Scheduled", color: "#f59e0b", bg: "#fffbeb", icon: Calendar },
  live:      { label: "Live Now",  color: "#16a34a", bg: "#f0fdf4", icon: Radio },
  ended:     { label: "Ended",     color: "#6b7280", bg: "#f3f4f6", icon: StopCircle },
};

function formatDate(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

function formatDuration(min) {
  if (!min) return "—";
  const h = Math.floor(min / 60), m = min % 60;
  return h > 0 ? `${h}h ${m > 0 ? m + "m" : ""}`.trim() : `${m}m`;
}

/* ── store pending leave in localStorage (unique key per record) ── */
const LEAVE_PREFIX = "lms_leave_";

function scheduleLeaveRecord(liveClassId, attendanceId, userId) {
  if (!attendanceId) return;
  const key = `${LEAVE_PREFIX}${attendanceId}`;
  // Store userId so flushPendingLeave only processes records for the current user
  localStorage.setItem(key, JSON.stringify({ liveClassId, attendanceId, userId, storedAt: Date.now() }));
  // sendBeacon fallback for abrupt browser/tab close
  const unloadHandler = () => {
    const url = `${BASE_URL}/lms/live-classes/${liveClassId}/attendance/${attendanceId}/leave`;
    navigator.sendBeacon(url, new Blob(["{}"], { type: "application/json" }));
    localStorage.removeItem(key);
  };
  window.addEventListener("beforeunload", unloadHandler);
}

function flushPendingLeave(currentUserId) {
  try {
    const keys = Object.keys(localStorage).filter((k) => k.startsWith(LEAVE_PREFIX));
    keys.forEach((key) => {
      try {
        const { liveClassId, attendanceId, userId, storedAt } = JSON.parse(localStorage.getItem(key));
        // If key has no userId (stale/old format), just remove it — don't flush
        if (!userId) { localStorage.removeItem(key); return; }
        // Only flush records belonging to the current logged-in user
        if (String(userId) !== String(currentUserId)) return;
        localStorage.removeItem(key);
        if (Date.now() - storedAt < 2 * 60 * 60 * 1000) {
          lmsRecordLeave(liveClassId, attendanceId).catch(() => {});
        }
      } catch {}
    });
  } catch {}
}

/* ── small input style ── */
const inp = (extra = {}) => ({
  width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
  border: "1.5px solid #e5e7eb", outline: "none", color: "#111827",
  background: "#fff", boxSizing: "border-box", ...extra,
});

/* ══ Schedule Modal ══ */
export function ScheduleModal({ onClose, onSave, schoolClasses, editItem, schoolId, prefill, chapters }) {
  const isEdit = !!editItem;
  // When prefill is provided (from course detail), class+subject are locked
  const isLocked = !isEdit && !!prefill?.class_id;

  // Convert UTC datetime string from DB to local datetime-local input format (YYYY-MM-DDTHH:mm)
  const toLocalInput = (utcStr) => {
    if (!utcStr) return "";
    const d = new Date(utcStr);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [form, setForm] = useState({
    title:            editItem?.title || prefill?.title || "",
    description:      editItem?.description || "",
    class_id:         editItem?.class_id || prefill?.class_id || "",
    subject_id:       editItem?.subject_id || prefill?.subject_id || "",
    chapter_id:       editItem?.chapter_id || prefill?.chapter_id || "",
    batch_id:         editItem?.batch_id || "",
    scheduled_at:     toLocalInput(editItem?.scheduled_at),
    end_datetime:     toLocalInput(editItem?.end_datetime),
    duration_minutes:  (() => {
      const s = editItem?.scheduled_at, e = editItem?.end_datetime;
      if (s && e) { const d = Math.round((new Date(e) - new Date(s)) / 60000); if (d > 0) return d; }
      return editItem?.duration_minutes || 0;
    })(),
    max_participants:   editItem?.max_participants || "",
    waiting_room:       editItem?.waiting_room || false,
    is_recorded:        editItem?.is_recorded || false,
  });
  const [saving, setSaving] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [batches, setBatches]   = useState([]);
  const [errors, setErrors]     = useState({});
  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "" })); // clear error on change
  };

  // Auto-fetch subjects and batches when class changes
  useEffect(() => {
    if (!form.class_id) {
      setSubjects([]);
      setBatches([]);
      if (!isLocked) { set("subject_id", ""); set("batch_id", ""); }
      return;
    }
    lmsGetClassSubjects({ class_id: form.class_id, school_id: schoolId })
      .then((res) => setSubjects(res.data.data || res.data || []))
      .catch(() => setSubjects([]));
    lmsGetBatches({ school_id: schoolId, class_id: form.class_id })
      .then((res) => setBatches(res.data.data || []))
      .catch(() => setBatches([]));
  }, [form.class_id, schoolId]);

  // Pre-load subjects when editing or when class is pre-filled (locked from course)
  useEffect(() => {
    const classId = editItem?.class_id || prefill?.class_id;
    if (classId && schoolId) {
      lmsGetClassSubjects({ class_id: classId, school_id: schoolId })
        .then((res) => setSubjects(res.data.data || res.data || []))
        .catch(() => {});
    }
  }, []);

  const handleSave = async () => {
    const errs = {};
    if (!form.title.trim())       errs.title            = "Class title is required";
    if (!form.description.trim()) errs.description      = "Description is required";
    if (!form.class_id)           errs.class_id         = "Please select a class";
    if (!form.subject_id)         errs.subject_id       = "Please select a subject";
    if (!form.scheduled_at)       errs.scheduled_at     = "Start date & time is required";
    if (!form.duration_minutes || form.duration_minutes <= 0) errs.duration_minutes = "Select start & end time to calculate duration";
    if (!form.end_datetime)
      errs.end_datetime = "End date & time is required";
    else if (form.scheduled_at && form.end_datetime <= form.scheduled_at)
      errs.end_datetime = "End date & time must be after the start date & time";
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save");
    } finally { setSaving(false); }
  };

  const lbl = (text, hint) => (
    <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
      {text}{hint && <span style={{ fontWeight: 400, color: "#9ca3af", marginLeft: 4 }}>{hint}</span>}
    </label>
  );
  const err = (key) => errors[key]
    ? <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>⚠ {errors[key]}</div>
    : null;
  const errBorder = (key) => errors[key] ? { border: "1.5px solid #dc2626", background: "#fff5f5" } : {};

  const lockedClass   = schoolClasses.find((c) => String(c.class_id ?? c.id) === String(form.class_id))?.class_name ?? form.class_id;
  const lockedSubject = subjects.find((s) => String(s.subject_id ?? s.id) === String(form.subject_id))?.subject_name ?? subjects[0]?.subject_name ?? form.subject_id;

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 18, width: 820, maxWidth: "96vw",
        boxShadow: "0 32px 80px rgba(0,0,0,0.22)", overflow: "hidden",
      }}>
        {/* Gradient bar */}
        <div style={{ height: 5, background: "linear-gradient(90deg,#4f46e5,#7c3aed,#06b6d4)" }} />

        {/* Header */}
        <div style={{ padding: "12px 20px 10px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg,#ede9fe,#dbeafe)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Video size={16} color="#4f46e5" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#111827" }}>{isEdit ? "Edit Live Class" : "Schedule Live Class"}</div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>DocMaster Session</div>
            </div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "#f1f5f9", borderRadius: 8, cursor: "pointer", padding: 6, display: "flex", color: "#6b7280" }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "12px 20px", display: "flex", flexDirection: "column", gap: 10, overflowY: "auto", maxHeight: "70vh" }}>

          {/* Row 1: Title */}
          <div>
            {lbl("Class Title", "*")}
            <input style={inp(errBorder("title"))} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Chapter 5 — Motion & Forces" />
            {err("title")}
          </div>

          {/* Row 2: Description | Class + Subject */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              {lbl("Description", "*")}
              <textarea style={inp({ resize: "none", height: 76, ...errBorder("description") })} value={form.description}
                onChange={(e) => set("description", e.target.value)} placeholder="What will be covered in this session?" />
              {err("description")}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                {lbl("Class", "*")}
                {isLocked
                  ? <div style={{ ...inp(), background: "#f8fafc", color: "#64748b", display: "flex", alignItems: "center", gap: 6 }}><BookOpen size={13} color="#94a3b8" />{lockedClass || "—"}</div>
                  : <select style={inp(errBorder("class_id"))} value={form.class_id} onChange={(e) => set("class_id", e.target.value)}>
                      <option value="">Select Class</option>
                      {schoolClasses.map((c) => <option key={c.class_id ?? c.id} value={String(c.class_id ?? c.id)}>{c.class_name ?? c.name}</option>)}
                    </select>
                }
                {err("class_id")}
              </div>
              <div>
                {lbl("Subject", "*")}
                {isLocked
                  ? <div style={{ ...inp(), background: "#f8fafc", color: "#64748b", display: "flex", alignItems: "center", gap: 6 }}><BookOpen size={13} color="#94a3b8" />{lockedSubject || "—"}</div>
                  : <select style={inp({ color: form.subject_id ? "#111827" : "#9ca3af", ...errBorder("subject_id") })} value={form.subject_id}
                      onChange={(e) => set("subject_id", e.target.value)} disabled={!form.class_id}>
                      <option value="">{form.class_id ? "Select Subject" : "Select class first"}</option>
                      {subjects.map((s) => <option key={s.subject_id ?? s.id} value={String(s.subject_id ?? s.id)}>{s.subject_name ?? s.name}</option>)}
                    </select>
                }
                {err("subject_id")}
              </div>
            </div>
          </div>

          {/* Row 3: Batch (conditional) */}
          {batches.length > 0 && (
            <div>
              {lbl("Batch", "(optional)")}
              <select style={inp()} value={form.batch_id} onChange={(e) => set("batch_id", e.target.value)}>
                <option value="">— No specific batch —</option>
                {batches.map((b) => <option key={b.id} value={String(b.id)}>{b.name}{b.section ? ` (${b.section})` : ""}</option>)}
              </select>
            </div>
          )}

          {/* Chapter (conditional) */}
          {chapters && chapters.length > 0 && (
            <div>
              {lbl("Chapter", "(optional)")}
              <select style={inp()} value={form.chapter_id} onChange={(e) => set("chapter_id", e.target.value)}>
                <option value="">No specific chapter</option>
                {chapters.map((ch) => <option key={ch.id} value={String(ch.id)}>{ch.title}</option>)}
              </select>
            </div>
          )}

          {/* Row 4: Start | End | Duration | Max Participants */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
            <div>
              {lbl("Start Date & Time", "*")}
              <input type="datetime-local" style={inp(errBorder("scheduled_at"))} value={form.scheduled_at}
                onChange={(e) => {
                  const val = e.target.value;
                  set("scheduled_at", val);
                  if (val && form.end_datetime) {
                    const diff = Math.round((new Date(form.end_datetime) - new Date(val)) / 60000);
                    if (diff > 0) setForm((f) => ({ ...f, scheduled_at: val, duration_minutes: diff }));
                  }
                }} />
              {err("scheduled_at")}
            </div>
            <div>
              {lbl("End Date & Time", "*")}
              <input type="datetime-local" style={inp(errBorder("end_datetime"))} value={form.end_datetime}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val && form.scheduled_at && val <= form.scheduled_at) {
                    setErrors((prev) => ({ ...prev, end_datetime: "End time must be after start time" }));
                    setForm((f) => ({ ...f, end_datetime: val }));
                  } else {
                    setErrors((prev) => { const n = { ...prev }; delete n.end_datetime; return n; });
                    const diff = val && form.scheduled_at ? Math.round((new Date(val) - new Date(form.scheduled_at)) / 60000) : null;
                    setForm((f) => ({ ...f, end_datetime: val, ...(diff && diff > 0 ? { duration_minutes: diff } : {}) }));
                  }
                }} />
              {err("end_datetime")}
            </div>
            <div>
              {lbl("Duration (min)", "*")}
              <input type="number" readOnly style={inp({ background: "#f8fafc", color: "#64748b", cursor: "not-allowed", ...errBorder("duration_minutes") })}
                value={form.duration_minutes} />
              {err("duration_minutes")}
            </div>
            <div>
              {lbl("Max Participants", "(optional)")}
              <input type="number" style={inp()} value={form.max_participants} min={1} placeholder="Unlimited"
                onChange={(e) => set("max_participants", e.target.value ? parseInt(e.target.value) : "")} />
            </div>
          </div>

          {/* Row 5: Waiting Room | Record */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {/* Waiting Room toggle */}
          <div onClick={() => set("waiting_room", !form.waiting_room)} style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, cursor: "pointer", padding: "7px 10px", borderRadius: 8,
            border: `1.5px solid ${form.waiting_room ? "#a5b4fc" : "#e5e7eb"}`, background: form.waiting_room ? "#eff6ff" : "#fafafa" }}>
            <input type="checkbox" checked={!!form.waiting_room} onChange={(e) => set("waiting_room", e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              style={{ width: 15, height: 15, accentColor: "#4f46e5", flexShrink: 0, margin: 0 }} />
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>Enable Waiting Room</span>
              <span style={{ fontSize: 10, color: "#9ca3af" }}>Students wait for approval</span>
            </div>
          </div>

          {/* Record toggle */}
          <div onClick={() => set("is_recorded", !form.is_recorded)} style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, cursor: "pointer", padding: "7px 10px", borderRadius: 8,
            border: `1.5px solid ${form.is_recorded ? "#86efac" : "#e5e7eb"}`, background: form.is_recorded ? "#f0fdf4" : "#fafafa" }}>
            <input type="checkbox" checked={!!form.is_recorded} onChange={(e) => set("is_recorded", e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              style={{ width: 15, height: 15, accentColor: "#16a34a", flexShrink: 0, margin: 0 }} />
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>Record this session</span>
              <span style={{ fontSize: 10, color: "#9ca3af" }}>Available after class ends</span>
            </div>
          </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 28px 22px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} style={{ padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600,
            border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{ padding: "10px 28px", borderRadius: 10, fontSize: 13, fontWeight: 700,
            border: "none", background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", cursor: saving ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 7, opacity: saving ? 0.8 : 1 }}>
            {saving ? <Loader size={14} className="ms2-spin" /> : <Video size={14} />}
            {isEdit ? "Update Class" : "Schedule Class"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══ Join Modal ══ */
export function JoinModal({ liveClass, onClose, studentMode = false, teacherMode = false }) {
  const getStoredInfo = () => {
    try {
      const u = JSON.parse(localStorage.getItem("userData") || "{}");
      // Detect role from userData
      let detectedRole = "attendee";
      if (u.teacher_id || u.role === "teacher") detectedRole = "moderator";
      else if (u.admin_id && !u.student_id && !u.parent_id) detectedRole = "moderator"; // admin
      else if (u.student_id) detectedRole = "attendee";

      // Resolve name
      let name = "";
      if (u.admin_name) name = u.admin_name;
      if (!name && u.student_details) {
        try {
          const sd = typeof u.student_details === "string" ? JSON.parse(u.student_details) : u.student_details;
          name = `${sd.first_name || ""} ${sd.last_name || ""}`.trim();
        } catch {}
      }
      if (!name) name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
      if (!name) name = u.name || u.username || u.school_name || "";

      return { name, detectedRole };
    } catch { return { name: "", detectedRole: "attendee" }; }
  };
  const { name: storedName, detectedRole } = getStoredInfo();
  const [fullName, setFullName]         = useState(storedName);
  const [role, setRole]                 = useState(teacherMode ? "moderator" : studentMode ? "attendee" : detectedRole);
  const [loading, setLoading]                 = useState(false);
  const [activeSession, setActiveSession]     = useState(null);
  const [confirmTakeover, setConfirmTakeover] = useState(false);
  const [waitingStatus, setWaitingStatus]     = useState(null); // null | "waiting" | "approved" | "rejected"
  const [pollRef, setPollRef]                 = useState(null);

  const u = (() => { try { return JSON.parse(localStorage.getItem("userData") || "{}"); } catch { return {}; } })();
  const userId = u.admin_id || u.student_id || u.id || null;
  const userSchoolId = u.school_id || u.admin_id || null;

  const doJoin = async (skipSessionCheck = false) => {
    setLoading(true);
    try {
      // School-level isolation
      if (userSchoolId && liveClass.school_id && String(liveClass.school_id) !== String(userSchoolId)) {
        toast.error("You are not authorized to join this class.");
        return;
      }

      // Block check — reject if user is on the blocked list
      if (userId) {
        try {
          const blockRes = await lmsGetBlockedUsers(liveClass.id);
          const blocked = blockRes.data?.data || blockRes.data || [];
          const isBlocked = blocked.some((b) => String(b.user_id) === String(userId));
          if (isBlocked) {
            toast.error("You have been blocked from joining this class.");
            return;
          }
        } catch {} // if endpoint unavailable, don't block the join
      }

      // One session check (skip when user confirmed takeover)
      if (!skipSessionCheck && userId) {
        try {
          const sRes = await lmsGetActiveSession(userId);
          const session = sRes.data?.data || sRes.data || null;
          const isDifferentClass = session && String(session.live_class_id) !== String(liveClass.id);
          const isValidSession = session && session.live_class_title; // null title = deleted/ended class = stale
          if (isDifferentClass && isValidSession) {
            setActiveSession(session);
            setConfirmTakeover(true);
            return;
          }
        } catch {} // if endpoint not available, continue
      }

      // End previous session if taking over
      if (skipSessionCheck && activeSession?.id) {
        lmsEndSession(activeSession.id).catch(() => {});
      }

      const res = await lmsGetLiveClassJoinUrl(liveClass.id, { full_name: fullName.trim(), role, user_id: userId });
      const { join_url } = res.data;
      if (!join_url) return toast.error("Could not get join URL — check server BBB config");

      // Record attendance then schedule leave recording on window focus
      lmsRecordAttendance(liveClass.id, {
        user_id: userId, user_name: fullName.trim(), role, school_id: userSchoolId,
      }).then((r) => {
        const attendanceId = r?.data?.id;
        if (attendanceId) scheduleLeaveRecord(liveClass.id, attendanceId, userId);
      }).catch(() => {});

      // Register new session (fire-and-forget)
      lmsRegisterSession({
        user_id: userId, live_class_id: liveClass.id, role,
        started_at: new Date().toISOString(),
      }).catch(() => {});

      window.open(join_url, "_blank", "noopener,noreferrer");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to get join link");
    } finally { setLoading(false); }
  };

  const handleJoin = () => {
    if (!fullName.trim()) return toast.error("Enter your name to join");
    doJoin(false);
  };

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, width: 420, maxWidth: "94vw",
        boxShadow: "0 24px 60px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#16a34a,#06b6d4)" }} />
        <div style={{ padding: "20px 22px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, background: "#f0fdf4",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Play size={18} color="#16a34a" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>Join Live Class</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>{liveClass.title}</div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                Your Full Name *
              </label>
              <input style={inp({ background: "#f3f4f6", color: "#6b7280", cursor: "not-allowed" })}
                value={fullName} readOnly disabled
                placeholder="Enter your name as it will appear in class" />
            </div>

            {false && !studentMode && !teacherMode && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                  Join As
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {[
                    { val: "attendee",  label: "Student / Viewer", icon: Users,   color: "#4f46e5" },
                    { val: "moderator", label: "Teacher / Host",   icon: Monitor, color: "#0891b2" },
                  ].map(({ val, label, icon: Icon, color }) => (
                    <label key={val} style={{
                      display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                      padding: "10px 12px", borderRadius: 9,
                      border: `2px solid ${role === val ? color : "#e5e7eb"}`,
                      background: role === val ? `${color}10` : "#fafafa",
                    }}>
                      <input type="radio" name="role" value={val} checked={role === val}
                        onChange={() => setRole(val)} style={{ accentColor: color }} />
                      <Icon size={14} color={color} />
                      <span style={{ fontSize: 12, fontWeight: 600, color: role === val ? color : "#6b7280" }}>
                        {label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Waiting room status */}
          {waitingStatus === "waiting" && (
            <div style={{ marginTop: 12, padding: "16px", borderRadius: 10, background: "#eff6ff", border: "1.5px solid #bfdbfe", textAlign: "center" }}>
              <Loader size={22} className="ms2-spin" color="#4f46e5" style={{ margin: "0 auto 8px" }} />
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1d4ed8" }}>Waiting for Approval</div>
              <div style={{ fontSize: 12, color: "#3b82f6", marginTop: 4 }}>
                The teacher is reviewing your request. Please wait...
              </div>
              <button onClick={() => { if (pollRef) clearInterval(pollRef); setWaitingStatus(null); }} style={{
                marginTop: 10, padding: "6px 14px", borderRadius: 8, border: "1.5px solid #93c5fd",
                background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#1d4ed8",
              }}>Cancel Request</button>
            </div>
          )}
          {waitingStatus === "rejected" && (
            <div style={{ marginTop: 12, padding: "14px", borderRadius: 10, background: "#fef2f2", border: "1.5px solid #fca5a5", textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626" }}>Request Rejected</div>
              <div style={{ fontSize: 12, color: "#ef4444", marginTop: 4 }}>
                The moderator has declined your request to join.
              </div>
            </div>
          )}
          {waitingStatus === "approved" && (
            <div style={{ marginTop: 12, padding: "14px", borderRadius: 10, background: "#f0fdf4", border: "1.5px solid #86efac", textAlign: "center" }}>
              <CheckCircle size={20} color="#16a34a" style={{ margin: "0 auto 6px" }} />
              <div style={{ fontSize: 13, fontWeight: 700, color: "#16a34a" }}>Approved! Opening class...</div>
            </div>
          )}

          {/* Session conflict warning */}
          {confirmTakeover && activeSession && (
            <div style={{
              marginTop: 12, padding: "12px 14px", borderRadius: 10,
              background: "#fff7ed", border: "1.5px solid #fdba74",
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#c2410c", marginBottom: 4 }}>
                ⚠ Active Session Detected
              </div>
              <div style={{ fontSize: 12, color: "#78350f", marginBottom: 10 }}>
                You are already in another live class: <strong>{activeSession.live_class_title || "another class"}</strong>.
                Joining here will end that session.
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { setConfirmTakeover(false); setActiveSession(null); }} style={{
                  flex: 1, padding: "7px", borderRadius: 8, border: "1.5px solid #e5e7eb",
                  background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#374151",
                }}>Stay in Current</button>
                <button onClick={() => { setConfirmTakeover(false); doJoin(true); }} style={{
                  flex: 1, padding: "7px", borderRadius: 8, border: "none",
                  background: "#ea580c", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer",
                }}>Join & End Previous</button>
              </div>
            </div>
          )}

          {/* Only show Join/Cancel when no conflict and not in waiting */}
          {!confirmTakeover && waitingStatus !== "waiting" && (
            <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
              <button onClick={onClose} style={{
                flex: 1, padding: "10px", borderRadius: 9, fontSize: 13, fontWeight: 600,
                border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", cursor: "pointer",
              }}>Cancel</button>
              <button onClick={handleJoin} disabled={loading || !!waitingStatus} style={{
                flex: 2, padding: "10px", borderRadius: 9, fontSize: 13, fontWeight: 700,
                border: "none", background: "linear-gradient(135deg,#16a34a,#059669)",
                color: "#fff", cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center",
                justifyContent: "center", gap: 6, opacity: loading ? 0.8 : 1,
              }}>
                {loading ? <Loader size={13} className="ms2-spin" /> : <ExternalLink size={13} />}
                Join Now
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══ Confirm Delete Modal ══ */
export function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div className="ms2-modal-overlay" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 14, width: 360, maxWidth: "92vw",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#dc2626,#ef4444)" }} />
        <div style={{ padding: "24px 22px", textAlign: "center" }}>
          <div style={{
            width: 48, height: 48, borderRadius: "50%", background: "#fef2f2",
            border: "2px solid #fecaca", display: "flex", alignItems: "center",
            justifyContent: "center", margin: "0 auto 12px",
          }}>
            <Trash2 size={20} color="#dc2626" />
          </div>
          <div style={{ fontSize: 14, color: "#374151" }}>{message}</div>
        </div>
        <div style={{ display: "flex", gap: 8, padding: "0 22px 18px" }}>
          <button onClick={onCancel} style={{
            flex: 1, padding: "9px", borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            flex: 1, padding: "9px", borderRadius: 8, fontSize: 13, fontWeight: 700,
            border: "none", background: "#dc2626", color: "#fff", cursor: "pointer",
          }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ══ Live Class Card ══ */
// studentMode  = student viewer (can only join when live)
// isAdminOrTeacher = show End Class + Edit + Delete buttons
/* ── Description preview with Read more modal ── */
function DescriptionPreview({ description }) {
  const [open, setOpen] = useState(false);
  if (!description) return null;
  return (
    <>
      <p className="lc-desc-clamp">{description}</p>
      {description.length > 80 && (
        <button className="lc-read-more" onClick={() => setOpen(true)}>Read more…</button>
      )}
      {open && (
        <div className="lc-modal-overlay" onClick={() => setOpen(false)}>
          <div className="lc-modal-box" onClick={e => e.stopPropagation()}>
            <p className="lc-modal-title">Description</p>
            <p className="lc-modal-body">{description}</p>
            <button className="lc-modal-close" onClick={() => setOpen(false)}>Close</button>
          </div>
        </div>
      )}
    </>
  );
}

export function LiveClassCard({
  item, onJoin, onEdit, onDelete, onRefreshStatus,
  studentMode = false, isAdminOrTeacher = false,
}) {
  const effectiveStatus = item.status === "live" ? "live"
    : (item.status === "ended" || (item.end_datetime && new Date(item.end_datetime) < new Date())) ? "ended"
    : "scheduled";
  const meta = STATUS_META[effectiveStatus] || STATUS_META.scheduled;
  const StatusIcon = meta.icon;
  const [checkingStatus, setCheckingStatus]   = useState(false);
  const [ending, setEnding]                   = useState(false);
  const [showAttendance, setShowAttendance]   = useState(false);
  const [showWaitingRoom, setShowWaitingRoom] = useState(false);
  const [showInvite, setShowInvite]           = useState(false);
  const [showPollQA, setShowPollQA]           = useState(false);
  const [showRecordings, setShowRecordings]   = useState(false);
  const [waitingCount, setWaitingCount]       = useState(0);
  const prevWaitingCount = React.useRef(0);
  const workerRef = React.useRef(null);

  // Request browser notification permission once
  useEffect(() => {
    if (isAdminOrTeacher && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, [isAdminOrTeacher]);

  // Auto-end when end_datetime is reached
  useEffect(() => {
    if (item.status !== "live" || !item.end_datetime || !isAdminOrTeacher) return;
    const ms = new Date(item.end_datetime) - Date.now();
    if (ms <= 0) {
      // Already past — end immediately
      lmsEndLiveClass(item.id).catch(() => {});
      lmsUpdateLiveClass(item.id, { status: "ended" }).catch(() => {});
      if (onRefreshStatus) onRefreshStatus();
      return;
    }
    const t = setTimeout(async () => {
      try {
        await lmsEndLiveClass(item.id);
      } catch {
        await lmsUpdateLiveClass(item.id, { status: "ended" }).catch(() => {});
      }
      if (onRefreshStatus) onRefreshStatus();
    }, ms);
    return () => clearTimeout(t);
  }, [item.id, item.status, item.end_datetime, isAdminOrTeacher, onRefreshStatus]);

  // Use Web Worker for polling — not throttled when tab is in background (e.g. teacher in BBB)
  useEffect(() => {
    if (!isAdminOrTeacher || item.status !== "live" || !item.waiting_room) return;

    const token = localStorage.getItem("userToken") || "";
    const apiBase = window.location.origin.includes("localhost")
      ? "http://localhost:5010"
      : window.location.origin;

    const worker = new Worker("/waiting-room-worker.js");
    workerRef.current = worker;

    worker.onmessage = (e) => {
      if (e.data.type !== "COUNT") return;
      const count = e.data.count;
      setWaitingCount(count);

      if (count > prevWaitingCount.current) {
        const newStudents = count - prevWaitingCount.current;
        // Desktop browser notification — shows on top of BBB tab
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification(`${newStudents} student${newStudents > 1 ? "s" : ""} waiting to join`, {
            body: `"${item.title}" — open the LMS portal to approve`,
            icon: "/favicon.ico",
            tag: `waiting-room-${item.id}`,
            requireInteraction: true, // stays until dismissed
          });
        }
        toast.info(`${newStudents} student${newStudents > 1 ? "s" : ""} waiting to join "${item.title}"`, {
          toastId: `wr-${item.id}`,
        });
      }
      prevWaitingCount.current = count;
    };

    worker.postMessage({ type: "START", payload: { liveClassId: item.id, apiBase, token } });

    return () => {
      worker.postMessage({ type: "STOP" });
      worker.terminate();
      workerRef.current = null;
    };
  }, [item.id, item.status, item.waiting_room, isAdminOrTeacher]);

  const handleCheckStatus = async () => {
    setCheckingStatus(true);
    try {
      const res = await lmsGetLiveClassStatus(item.id);
      const { is_running } = res.data;
      toast.info(is_running ? "Meeting is currently LIVE on BBB" : "Meeting is not running on BBB");
      if (onRefreshStatus) onRefreshStatus();
    } catch { toast.error("Could not check status"); }
    finally { setCheckingStatus(false); }
  };

  const handleEnd = async () => {
    if (!window.confirm(`End "${item.title}"? This will close the meeting for all participants.`)) return;
    setEnding(true);
    try {
      await lmsEndLiveClass(item.id);
      // Also update status to ended in DB
      await lmsUpdateLiveClass(item.id, { status: "ended" }).catch(() => {});
      toast.success("Live class ended");
      if (onRefreshStatus) onRefreshStatus();
    } catch (err) {
      // If dedicated end endpoint fails, try updating status directly
      try {
        await lmsUpdateLiveClass(item.id, { status: "ended" });
        toast.success("Class marked as ended");
        if (onRefreshStatus) onRefreshStatus();
      } catch {
        toast.error("Failed to end class");
      }
    } finally { setEnding(false); }
  };

  const isLive      = item.status === "live";
  const isEnded     = item.status === "ended";
  const isScheduled = item.status === "scheduled";
  const now = new Date();
  const isExpiredByDate = !isLive && item.end_datetime && new Date(item.end_datetime) < now;
  const isNotStartedYet = item.scheduled_at && new Date(item.scheduled_at) > now;
  // Allow joining only within 5 min before scheduled time
  const scheduledMs = item.scheduled_at ? new Date(item.scheduled_at) - now : 0;
  const tooEarlyToStart = isScheduled && scheduledMs > 5 * 60 * 1000;

  const enrolled = item.student_join_count ?? 0;
  const capacity = item.max_participants || null;
  const fillPct  = capacity ? Math.min(100, Math.round((enrolled / capacity) * 100)) : 0;
  const cardState = isLive ? "live" : (isEnded || isExpiredByDate) ? "ended" : "scheduled";

  return (
    <article className="lc-card" data-state={cardState}>

      {/* ── Header ── */}
      <div className="lc-head">
        <div className="lc-top">
          <span className="lc-pill">
            <span className="lc-dot" />
            {meta.label}
          </span>
          {item.batch_name && <span className="lc-batch">{item.batch_name}</span>}
          {item.is_recorded === 1 && (
            <span className="lc-rec"><span className="lc-rec-dot" />Recorded</span>
          )}
        </div>
        <h3 className="lc-title">{item.title}</h3>
        {item.scheduled_at && (
          <div className="lc-when">
            <span><Calendar size={11} />{new Date(item.scheduled_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
            <span><Clock size={11} />{new Date(item.scheduled_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}</span>
            {item.end_datetime && (
              <>
                <span style={{ opacity: 0.7 }}>→</span>
                <span><Calendar size={11} />{new Date(item.end_datetime).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
                <span><Clock size={11} />{new Date(item.end_datetime).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Body ── */}
      <div className="lc-body">
        <DescriptionPreview description={item.description} />

        <div className="lc-stats">
          <div className="lc-stat">
            <div className="lc-ic"><Users size={14} /></div>
            <div className="lc-stat-info">
              <b className="lc-stat-val">{enrolled}</b>
              <span className="lc-stat-label">Enrolled</span>
            </div>
          </div>
          <div className="lc-stat">
            <div className="lc-ic"><Clock size={14} /></div>
            <div className="lc-stat-info">
              <b className="lc-stat-val">{item.duration_minutes || "–"}<small>{item.duration_minutes ? " min" : ""}</small></b>
              <span className="lc-stat-label">Duration</span>
            </div>
          </div>
          <div className="lc-stat">
            <div className="lc-ic"><Monitor size={14} /></div>
            <div className="lc-stat-info">
              <b className="lc-stat-val">{capacity || "∞"}</b>
              <span className="lc-stat-label">Capacity</span>
            </div>
          </div>
        </div>

        {capacity && (
          <div className="lc-seats">
            <div className="lc-seats-header"><span>Seats filled</span><span>{enrolled} of {capacity}</span></div>
            <div className="lc-bar"><div className="lc-bar-fill" style={{ width: `${fillPct}%` }} /></div>
          </div>
        )}

        <div className="lc-tags">
          {item.class_name   && <span className="lc-tag">{item.class_name}</span>}
          {item.subject_name && <span className="lc-tag">{item.subject_name}</span>}

          {!studentMode && (
            <div style={{ marginLeft: "auto", display: "flex", gap: 5 }}>
              <button className="lc-icon-btn lc-icon-btn--sync" onClick={handleCheckStatus} disabled={checkingStatus} title="Sync BBB status" style={{ width: 30, height: 30, borderRadius: 8 }}>
                {checkingStatus ? <Loader size={13} className="ms2-spin" /> : <RefreshCw size={13} />}
              </button>
              {!!item.waiting_room && isLive && (
                <button className="lc-icon-btn lc-icon-btn--waiting" onClick={() => setShowWaitingRoom(true)} title="Waiting Room" style={{ width: 30, height: 30, borderRadius: 8 }}>
                  <DoorOpen size={13} />
                  {waitingCount > 0 && <span className="lc-badge">{waitingCount}</span>}
                </button>
              )}
              {isLive && (
                <button className="lc-icon-btn lc-icon-btn--poll" onClick={() => setShowPollQA(true)} title="Polls & Q&A" style={{ width: 30, height: 30, borderRadius: 8 }}>
                  <BarChart2 size={13} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="lc-foot">

        {/* STUDENT */}
        {studentMode ? (
          (isEnded || isExpiredByDate) ? (
            <div className="lc-btn lc-btn--ended"><StopCircle size={15} /> Class Ended</div>
          ) : isLive ? (
            <button className="lc-btn lc-btn--live" onClick={() => onJoin(item)}><Play size={15} /> Join Live</button>
          ) : (
            <div className="lc-btn lc-btn--scheduled"><Calendar size={14} /> Scheduled — Not started yet</div>
          )
        ) : (
          /* ADMIN / TEACHER */
          <>
            {(isEnded || isExpiredByDate)
              ? <div className="lc-btn lc-btn--ended"><StopCircle size={15} /> Class Ended</div>
              : tooEarlyToStart
              ? <div className="lc-btn lc-btn--scheduled" title={`Class starts at ${new Date(item.scheduled_at).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",hour12:true})}`}>
                  <Clock size={14} /> Starts at {new Date(item.scheduled_at).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",hour12:true})}
                </div>
              : <button className={`lc-btn${isLive ? " lc-btn--live" : ""}`} onClick={() => onJoin(item)}>
                  <Play size={15} /> {isLive ? "Join Live" : "Start Class"}
                </button>
            }

            {isLive && !isExpiredByDate && (
              <button className="lc-btn lc-btn--end-class" onClick={handleEnd} disabled={ending} title="End Class">
                {ending ? <Loader size={13} className="ms2-spin" /> : "End"}
              </button>
            )}

            <button className="lc-icon-btn lc-icon-btn--attend" onClick={() => setShowAttendance(true)} title="Attendance"><ClipboardList size={15} /></button>
            {item.is_recorded === 1 && (
              <button className="lc-icon-btn" onClick={() => setShowRecordings(true)} title="View Recordings"
                style={{ background: "#f0fdf4", color: "#16a34a", border: "1.5px solid #86efac" }}>
                <Film size={15} />
              </button>
            )}

            {isAdminOrTeacher && (
              <button className="lc-icon-btn lc-icon-btn--invite" onClick={() => setShowInvite(true)} title="Invite by Email"><Users size={15} /></button>
            )}

            {onEdit && (
              <button className="lc-icon-btn lc-icon-btn--edit" onClick={() => onEdit(item)} title="Edit"><BookOpen size={15} /></button>
            )}

            {onDelete && (
              <button className="lc-icon-btn lc-icon-btn--danger" onClick={() => onDelete(item)} title="Delete"><Trash2 size={15} /></button>
            )}
          </>
        )}
      </div>
      {showAttendance && (
        <AttendanceDrawer liveClass={item} onClose={() => setShowAttendance(false)} />
      )}
      {showWaitingRoom && (
        <WaitingRoomDrawer
          liveClass={item}
          onClose={() => { setShowWaitingRoom(false); }}
          onCountChange={setWaitingCount}
        />
      )}
      {showInvite && (
        <InviteDrawer liveClass={item} onClose={() => setShowInvite(false)} />
      )}
      {showPollQA && (
        <PollQADrawer liveClass={item} isAdminOrTeacher={isAdminOrTeacher} onClose={() => setShowPollQA(false)} />
      )}
      {showRecordings && (
        <RecordingsDrawer liveClass={item} onClose={() => setShowRecordings(false)} />
      )}
    </article>
  );
}

/* ══════════════════════════════
   RECORDINGS DRAWER
══════════════════════════════ */
function RecordingsDrawer({ liveClass, onClose }) {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [saving, setSaving]         = useState({});   // recordId → true/false
  const [playing, setPlaying]       = useState(null); // local_url to play

  const load = () => {
    setLoading(true);
    lmsGetLiveClassRecordings(liveClass.id)
      .then(res => setRecordings(res.data?.recordings || []))
      .catch(() => setError("Could not load recordings."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [liveClass.id]);

  const fmtDuration = (ms) => {
    if (!ms) return "—";
    const totalMin = Math.floor(Number(ms) / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const handleSave = async (rec) => {
    if (!rec.bbb_url) return toast.error("No downloadable URL found (presentation format only)");
    setSaving(s => ({ ...s, [rec.record_id]: true }));
    try {
      await lmsDownloadRecording(liveClass.id, rec.record_id, rec.bbb_url);
      toast.info("Saving to DocMaster server… This may take a few minutes.");
      // Poll every 5s until saved
      const poll = setInterval(async () => {
        try {
          const r = await lmsRecordingStatus(rec.record_id);
          if (r.data.saved) {
            clearInterval(poll);
            setSaving(s => ({ ...s, [rec.record_id]: false }));
            toast.success("Recording saved to DocMaster!");
            load();
          }
        } catch { clearInterval(poll); setSaving(s => ({ ...s, [rec.record_id]: false })); }
      }, 5000);
    } catch {
      toast.error("Failed to start download.");
      setSaving(s => ({ ...s, [rec.record_id]: false }));
    }
  };

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 1100, display: "flex", alignItems: "flex-end", justifyContent: "center", background: "rgba(0,0,0,0.45)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#fff", borderRadius: "16px 16px 0 0", width: "100%", maxWidth: 680,
        maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 -8px 32px rgba(0,0,0,0.15)",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 20px", borderBottom: "1px solid #e5e7eb" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#111827", display: "flex", alignItems: "center", gap: 8 }}>
              <Film size={16} color="#16a34a" /> Recordings
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{liveClass.title}</div>
          </div>
          <button onClick={onClose} style={{ background: "#f3f4f6", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", display: "grid", placeItems: "center" }}>
            <X size={15} />
          </button>
        </div>

        {/* Inline video player */}
        {playing && (
          <div style={{ background: "#000", position: "relative" }}>
            <video src={playing} controls autoPlay style={{ width: "100%", maxHeight: 320, display: "block" }} />
            <button onClick={() => setPlaying(null)} style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.6)", border: "none", borderRadius: 6, color: "#fff", padding: "4px 10px", cursor: "pointer", fontSize: 12 }}>
              Close Player
            </button>
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
              <Loader size={28} className="ms2-spin" color="#4f46e5" />
            </div>
          ) : error ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#dc2626", fontSize: 14 }}>{error}</div>
          ) : recordings.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <Film size={40} color="#e5e7eb" style={{ margin: "0 auto 12px", display: "block" }} />
              <div style={{ fontWeight: 600, color: "#6b7280", fontSize: 14 }}>No recordings available yet</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>BBB processes recordings after class ends. This may take a few minutes.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {recordings.map((rec, i) => (
                <div key={rec.record_id || i} style={{ border: "1px solid #e5e7eb", borderRadius: 12, background: rec.saved_locally ? "#f0fdf4" : "#f9fafb", overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px" }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: rec.saved_locally ? "#dcfce7" : "#e0e7ff", color: rec.saved_locally ? "#16a34a" : "#4f46e5", display: "grid", placeItems: "center", flexShrink: 0 }}>
                      <Film size={20} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: "#111827", display: "flex", alignItems: "center", gap: 8 }}>
                        Recording {i + 1}
                        {rec.saved_locally && <span style={{ fontSize: 11, background: "#dcfce7", color: "#16a34a", padding: "2px 8px", borderRadius: 99, fontWeight: 700 }}>Saved on DocMaster</span>}
                        {rec.format === "video" && !rec.saved_locally && <span style={{ fontSize: 11, background: "#e0e7ff", color: "#4f46e5", padding: "2px 8px", borderRadius: 99 }}>MP4 available</span>}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3, display: "flex", gap: 14 }}>
                        {rec.start_time && <span>{new Date(Number(rec.start_time)).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>}
                        {rec.duration && <span>{fmtDuration(rec.duration)}</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                      {/* Play from DocMaster (local MP4) */}
                      {rec.saved_locally && rec.local_url && (
                        <button onClick={() => setPlaying(rec.local_url)}
                          style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 14px", borderRadius: 8, background: "#16a34a", color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                          <Play size={13} /> Play
                        </button>
                      )}
                      {/* Save to DocMaster */}
                      {!rec.saved_locally && rec.format === "video" && (
                        <button onClick={() => handleSave(rec)} disabled={saving[rec.record_id]}
                          style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 14px", borderRadius: 8, background: "#4f46e5", color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving[rec.record_id] ? 0.7 : 1 }}>
                          {saving[rec.record_id] ? <><Loader size={12} className="ms2-spin" /> Saving…</> : "Save to DocMaster"}
                        </button>
                      )}
                      {/* Open in BBB (presentation format or fallback) */}
                      {rec.bbb_url && (
                        <a href={rec.bbb_url} target="_blank" rel="noopener noreferrer"
                          style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 14px", borderRadius: 8, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
                          <ExternalLink size={12} /> BBB Player
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ══════════════════════════════
   ATTENDANCE DRAWER
══════════════════════════════ */
export function AttendanceDrawer({ liveClass, onClose }) {
  const [records, setRecords]       = useState([]);
  const [blockedIds, setBlockedIds] = useState(new Set());
  const [loading, setLoading]       = useState(true);
  const [actionId, setActionId]     = useState(null); // userId being actioned

  const loadData = () => {
    setLoading(true);
    Promise.all([
      lmsGetAttendance(liveClass.id).catch(() => ({ data: [] })),
      lmsGetBlockedUsers(liveClass.id).catch(() => ({ data: [] })),
    ]).then(([attRes, blockRes]) => {
      setRecords(attRes.data?.data || attRes.data || []);
      const blocked = blockRes.data?.data || blockRes.data || [];
      setBlockedIds(new Set(blocked.map((b) => String(b.user_id))));
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, [liveClass.id]);

  const handleBlock = async (r) => {
    if (!window.confirm(`Block ${r.user_name} from this class?`)) return;
    setActionId(r.user_id);
    try {
      await lmsBlockParticipant(liveClass.id, { user_id: r.user_id, user_name: r.user_name });
      // Also kick them from active BBB meeting
      await lmsKickParticipant(liveClass.id, { user_id: r.user_id, full_name: r.user_name }).catch(() => {});
      toast.success(`${r.user_name} blocked`);
      loadData();
    } catch { toast.error("Failed to block participant"); }
    finally { setActionId(null); }
  };

  const handleUnblock = async (r) => {
    setActionId(r.user_id);
    try {
      await lmsUnblockParticipant(liveClass.id, r.user_id);
      toast.success(`${r.user_name} unblocked`);
      loadData();
    } catch { toast.error("Failed to unblock"); }
    finally { setActionId(null); }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  };

  return createPortal(
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 2000,
      background: "rgba(15,23,42,0.5)", display: "flex", justifyContent: "flex-end",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 420, maxWidth: "95vw", background: "#fff", height: "100%",
        overflowY: "auto", boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ padding: "18px 20px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 10 }}>
          <ClipboardList size={18} color="#16a34a" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>Attendance Log</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>{liveClass.title}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280" }}>
            <X size={18} />
          </button>
        </div>

        {/* Stats */}
        <div style={{ padding: "12px 20px", background: "#f8fafc", borderBottom: "1px solid #e5e7eb", display: "flex", gap: 20 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#4f46e5" }}>{records.length}</div>
            <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>JOINED</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#16a34a" }}>
              {records.filter((r) => r.role === "attendee").length}
            </div>
            <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>STUDENTS</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#0891b2" }}>
              {records.filter((r) => r.role === "moderator").length}
            </div>
            <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>TEACHERS</div>
          </div>
        </div>

        {/* Records */}
        <div style={{ flex: 1, padding: "12px 20px" }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
              <Loader size={24} className="ms2-spin" color="#4f46e5" />
            </div>
          ) : records.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af", fontSize: 13 }}>
              No attendance records yet.
            </div>
          ) : (
            records.map((r, i) => {
              const isBlocked = blockedIds.has(String(r.user_id));
              const isActioning = actionId === r.user_id;
              return (
                <div key={r.id || i} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 0", borderBottom: "1px solid #f1f5f9",
                  opacity: isBlocked ? 0.6 : 1,
                }}>
                  {/* Avatar */}
                  <div style={{
                    width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                    background: isBlocked ? "#fee2e2" : r.role === "moderator" ? "#e0f2fe" : "#dcfce7",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: 700,
                    color: isBlocked ? "#dc2626" : r.role === "moderator" ? "#0891b2" : "#16a34a",
                  }}>
                    {isBlocked ? <Ban size={14} /> : (r.user_name || "?")[0].toUpperCase()}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", display: "flex", alignItems: "center", gap: 6 }}>
                      {r.user_name || "Unknown"}
                      {isBlocked && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", background: "#fee2e2", padding: "1px 6px", borderRadius: 10 }}>
                          BLOCKED
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>
                      {r.role === "moderator" ? "Teacher" : "Student"} · Joined {fmt(r.joined_at)}
                    </div>
                    {r.left_at && (
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>Left {fmt(r.left_at)}</div>
                    )}
                  </div>

                  {/* Block / Unblock — skip for moderators */}
                  {r.role !== "moderator" && (
                    <button
                      onClick={() => isBlocked ? handleUnblock(r) : handleBlock(r)}
                      disabled={isActioning}
                      title={isBlocked ? "Unblock" : "Block & Kick"}
                      style={{
                        flexShrink: 0, padding: "5px 8px", borderRadius: 7, border: "none",
                        background: isBlocked ? "#f0fdf4" : "#fff5f5",
                        color: isBlocked ? "#16a34a" : "#dc2626",
                        cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
                        fontSize: 11, fontWeight: 700,
                      }}
                    >
                      {isActioning
                        ? <Loader size={12} className="ms2-spin" />
                        : isBlocked ? <><ShieldOff size={12} /> Unblock</> : <><UserX size={12} /> Block</>
                      }
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ══════════════════════════════
   WAITING ROOM DRAWER
══════════════════════════════ */
export function WaitingRoomDrawer({ liveClass, onClose, onCountChange }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [actionId, setActionId] = useState(null);

  const load = () => {
    lmsGetWaitingRoom(liveClass.id)
      .then((res) => {
        const all = res.data?.data || res.data || [];
        const pending = all.filter((r) => r.status === "pending");
        setRequests(all);
        if (onCountChange) onCountChange(pending.length);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [liveClass.id]);

  const handleApprove = async (r) => {
    setActionId(r.id);
    try {
      await lmsApproveWaitingUser(liveClass.id, r.id);
      toast.success(`${r.user_name} approved`);
      load();
    } catch { toast.error("Failed to approve"); }
    finally { setActionId(null); }
  };

  const handleReject = async (r) => {
    setActionId(r.id);
    try {
      await lmsRejectWaitingUser(liveClass.id, r.id);
      toast.info(`${r.user_name} rejected`);
      load();
    } catch { toast.error("Failed to reject"); }
    finally { setActionId(null); }
  };

  const pendingCount = requests.filter((r) => r.status === "pending").length;

  return createPortal(
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 2000,
      background: "rgba(15,23,42,0.5)", display: "flex", justifyContent: "flex-end",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 400, maxWidth: "95vw", background: "#fff", height: "100%",
        overflowY: "auto", boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ padding: "18px 20px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 10 }}>
          <DoorOpen size={18} color="#4f46e5" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>
              Waiting Room
              {pendingCount > 0 && (
                <span style={{ marginLeft: 8, background: "#dc2626", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>
                  {pendingCount} pending
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>{liveClass.title}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280" }}>
            <X size={18} />
          </button>
        </div>

        {/* List */}
        <div style={{ flex: 1, padding: "12px 20px" }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
              <Loader size={24} className="ms2-spin" color="#4f46e5" />
            </div>
          ) : requests.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af", fontSize: 13 }}>
              No one is waiting to join.
            </div>
          ) : (
            requests.map((r, i) => {
              const isPending  = r.status === "pending";
              const isApproved = r.status === "approved";
              const actioning  = actionId === r.id;
              return (
                <div key={r.id || i} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "12px 0", borderBottom: "1px solid #f1f5f9",
                }}>
                  {/* Avatar */}
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                    background: isApproved ? "#dcfce7" : isPending ? "#eff6ff" : "#fee2e2",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: 700,
                    color: isApproved ? "#16a34a" : isPending ? "#4f46e5" : "#dc2626",
                  }}>
                    {(r.user_name || "?")[0].toUpperCase()}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{r.user_name}</div>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>
                      {r.status === "approved" ? "✓ Approved" : r.status === "rejected" ? "✗ Rejected" : "Waiting..."}
                    </div>
                  </div>

                  {/* Approve / Reject — only for pending */}
                  {isPending && (
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button onClick={() => handleApprove(r)} disabled={actioning} style={{
                        padding: "5px 10px", borderRadius: 7, border: "none",
                        background: "#16a34a", color: "#fff",
                        fontSize: 11, fontWeight: 700, cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 4,
                      }}>
                        {actioning ? <Loader size={11} className="ms2-spin" /> : <CheckCircle size={11} />} Allow
                      </button>
                      <button onClick={() => handleReject(r)} disabled={actioning} style={{
                        padding: "5px 10px", borderRadius: 7, border: "none",
                        background: "#fee2e2", color: "#dc2626",
                        fontSize: 11, fontWeight: 700, cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 4,
                      }}>
                        <X size={11} /> Deny
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ══════════════════════════════
   MAIN PAGE
══════════════════════════════ */
export default function LiveClasses() {
  const dispatch = useDispatch();
  const schoolIdFromRedux = useSelector((s) => s.auth?.user?.school_id);
  const schoolId = schoolIdFromRedux || (() => {
    try {
      const u = JSON.parse(localStorage.getItem("userData") || "{}");
      return u.school_id || u.admin_id || null;
    } catch { return null; }
  })();
  const schoolClasses = useSelector((s) => s.lms?.schoolClasses || []);
  const currentUserId = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("userData") || "{}");
      return u.admin_id || u.student_id || u.id || null;
    } catch { return null; }
  })();

  const [classes, setClasses]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filter, setFilter]           = useState("all");     // all | scheduled | live | ended
  const [showSchedule, setShowSchedule] = useState(false);
  const [editItem, setEditItem]         = useState(null);
  const [joinItem, setJoinItem]         = useState(null);
  const [confirmDel, setConfirmDel]     = useState(null);

  // Advanced filters
  const [filterClass,    setFilterClass]    = useState("");
  const [filterBatch,    setFilterBatch]    = useState("");
  const [filterSubject,  setFilterSubject]  = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo,   setFilterDateTo]   = useState("");
  const [filterSubjects, setFilterSubjects] = useState([]);
  const [filterBatches,  setFilterBatches]  = useState([]);

  const [notifDenied,  setNotifDenied]  = useState(false);
  const [notifDefault, setNotifDefault] = useState(false);

  useEffect(() => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "denied") setNotifDenied(true);
    else if (Notification.permission === "default") setNotifDefault(true);
  }, []);

  const handleEnableNotifications = () => {
    Notification.requestPermission().then((p) => {
      if (p === "granted") setNotifDefault(false);
      else if (p === "denied") { setNotifDefault(false); setNotifDenied(true); }
    });
  };

  // Fetch subjects & batches when class filter changes
  useEffect(() => {
    setFilterSubject("");
    setFilterBatch("");
    setFilterSubjects([]);
    setFilterBatches([]);
    if (!filterClass) return;
    lmsGetClassSubjects({ class_id: filterClass, school_id: schoolId })
      .then((r) => setFilterSubjects(r.data?.data || r.data || []))
      .catch(() => {});
    lmsGetBatches({ class_id: filterClass, school_id: schoolId })
      .then((r) => setFilterBatches(r.data?.data || r.data || []))
      .catch(() => {});
  }, [filterClass, schoolId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { school_id: schoolId };
      if (filter !== "all") params.status = filter;
      const res = await lmsGetLiveClasses(params);
      setClasses(res.data.data || []);
    } catch { toast.error("Failed to load live classes"); }
    finally { setLoading(false); }
  }, [schoolId, filter]);

  // Client-side filtered list
  const filteredClasses = classes.filter((c) => {
    if (filterClass   && String(c.class_id)   !== String(filterClass))   return false;
    if (filterBatch   && String(c.batch_id)   !== String(filterBatch))   return false;
    if (filterSubject && String(c.subject_id) !== String(filterSubject)) return false;
    if (filterDateFrom) {
      const classDate = new Date(c.scheduled_at);
      if (classDate < new Date(filterDateFrom)) return false;
    }
    if (filterDateTo) {
      const classDate = new Date(c.scheduled_at);
      const toEnd = new Date(filterDateTo);
      toEnd.setHours(23, 59, 59, 999);
      if (classDate > toEnd) return false;
    }
    return true;
  });

  const hasFilters = filterClass || filterBatch || filterSubject || filterDateFrom || filterDateTo;
  const clearFilters = () => {
    setFilterClass(""); setFilterBatch(""); setFilterSubject("");
    setFilterDateFrom(""); setFilterDateTo("");
  };

  useEffect(() => {
    // Auto-sync BBB status on every page load/refresh
    lmsSyncLiveClassStatus().catch(() => {});
    // If returning from BBB (new tab redirected here), flush this user's pending leave
    const bbbExitClassId = new URLSearchParams(window.location.search).get("bbb_exit");
    if (bbbExitClassId) {
      flushPendingLeave(currentUserId);
      window.history.replaceState({}, "", window.location.pathname);
    }
    load();
  }, [load, currentUserId]);
  useEffect(() => { if (schoolId) dispatch(fetchLmsSchoolClasses(schoolId)); }, [schoolId, dispatch]);

  // When user switches back to this tab, sync BBB status
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        lmsSyncLiveClassStatus().then(() => load()).catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [load]);


  const handleSave = async (form) => {
    if (editItem) {
      await lmsUpdateLiveClass(editItem.id, { ...form, school_id: schoolId });
      toast.success("Class updated");
    } else {
      await lmsCreateLiveClass({ ...form, school_id: schoolId });
      toast.success("Class scheduled!");
    }
    setEditItem(null);
    load();
  };

  const handleDelete = async () => {
    try {
      await lmsDeleteLiveClass(confirmDel.id);
      toast.success("Class deleted");
      setConfirmDel(null);
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const liveCount      = classes.filter((c) => c.status === "live").length;
  const scheduledCount = classes.filter((c) => c.status === "scheduled").length;

  return (
    <div className="lms-content-inner">
      {/* Notification permission banner */}
      {notifDefault && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10, padding: "10px 16px",
          background: "#eff6ff", border: "1.5px solid #bfdbfe", borderRadius: 10,
          marginBottom: 16, fontSize: 13, color: "#1e40af",
        }}>
          <span style={{ fontSize: 16 }}>🔔</span>
          <span style={{ flex: 1 }}>
            <strong>Enable notifications</strong> to get alerts when students join the waiting room.
          </span>
          <button onClick={handleEnableNotifications} style={{
            padding: "6px 14px", borderRadius: 7, border: "none",
            background: "#2563eb", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer",
          }}>Allow</button>
        </div>
      )}
      {notifDenied && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10, padding: "10px 16px",
          background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: 10,
          marginBottom: 16, fontSize: 13, color: "#92400e",
        }}>
          <span style={{ fontSize: 16 }}>🔔</span>
          <span style={{ flex: 1 }}>
            <strong>Notifications blocked.</strong> Go to browser Site Settings → Notifications → Allow.
          </span>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 20, color: "#111827" }}>Live Classes</div>
        </div>
        {liveCount > 0 && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#16a34a" }}>{liveCount}</div>
            <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>LIVE NOW</div>
          </div>
        )}
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#4f46e5" }}>{scheduledCount}</div>
          <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>UPCOMING</div>
        </div>
        <button onClick={() => { setEditItem(null); setShowSchedule(true); }} style={{
          display: "flex", alignItems: "center", gap: 7, padding: "10px 18px",
          borderRadius: 10, border: "none",
          background: "linear-gradient(135deg,#4f46e5,#06b6d4)",
          color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
        }}>
          <Plus size={15} /> Schedule Class
        </button>
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {[
          { val: "all",       label: "All Classes" },
          { val: "live",      label: "🔴 Live Now" },
          { val: "scheduled", label: "Upcoming" },
          { val: "ended",     label: "Ended" },
        ].map(({ val, label }) => (
          <button key={val} onClick={() => setFilter(val)} style={{
            padding: "7px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: `1.5px solid ${filter === val ? "#4f46e5" : "#e5e7eb"}`,
            background: filter === val ? "#4f46e5" : "#fff",
            color: filter === val ? "#fff" : "#6b7280",
            cursor: "pointer",
          }}>{label}</button>
        ))}
        <button onClick={load} style={{
          marginLeft: "auto", padding: "7px 12px", borderRadius: 8,
          border: "1.5px solid #e5e7eb", background: "#fff", color: "#6b7280",
          cursor: "pointer", display: "flex", alignItems: "center", gap: 5, fontSize: 13,
        }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Advanced Filters */}
      <div className="crs-filters" style={{ marginBottom: 16, flexWrap: "wrap" }}>
        <select className="crs-filter-select" style={{ flex: "1 1 0", width: 0, minWidth: 110 }}
          value={filterClass} onChange={(e) => setFilterClass(e.target.value)}>
          <option value="">All Classes</option>
          {schoolClasses.map((c) => (
            <option key={c.class_id ?? c.id} value={String(c.class_id ?? c.id)}>{c.class_name ?? c.name}</option>
          ))}
        </select>

        <select className="crs-filter-select" style={{ flex: "1 1 0", width: 0, minWidth: 110, opacity: filterClass ? 1 : 0.5 }}
          value={filterBatch} onChange={(e) => setFilterBatch(e.target.value)} disabled={!filterClass}>
          <option value="">All Batches</option>
          {filterBatches.map((b) => (
            <option key={b.id} value={String(b.id)}>{b.name}{b.section ? ` (${b.section})` : ""}</option>
          ))}
        </select>

        <select className="crs-filter-select" style={{ flex: "1 1 0", width: 0, minWidth: 110, opacity: filterClass ? 1 : 0.5 }}
          value={filterSubject} onChange={(e) => setFilterSubject(e.target.value)} disabled={!filterClass}>
          <option value="">All Subjects</option>
          {filterSubjects.map((s) => (
            <option key={s.subject_id ?? s.id} value={String(s.subject_id ?? s.id)}>{s.subject_name ?? s.name}</option>
          ))}
        </select>

        <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>From</span>
        <input type="date" className="crs-filter-select" style={{ flex: "1 1 0", width: 0, minWidth: 130 }}
          value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} />

        <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>To</span>
        <input type="date" className="crs-filter-select" style={{ flex: "1 1 0", width: 0, minWidth: 130 }}
          value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} />

        {hasFilters && (
          <>
            <button onClick={clearFilters} style={{
              padding: "7px 14px", borderRadius: 8, border: "1px solid #fca5a5",
              background: "#fff7f7", color: "#ef4444", fontSize: 13, fontWeight: 600,
              cursor: "pointer", whiteSpace: "nowrap",
            }}>✕ Clear</button>
            <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>
              {filteredClasses.length} of {classes.length} classes
            </span>
          </>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <Loader size={28} className="ms2-spin" color="#4f46e5" />
        </div>
      ) : filteredClasses.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "60px 20px",
          background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb",
        }}>
          <Video size={48} color="#e5e7eb" style={{ margin: "0 auto 16px" }} />
          <div style={{ fontSize: 16, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
            {hasFilters ? "No classes match the selected filters" : filter === "live" ? "No live sessions right now" : "No classes found"}
          </div>
          <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20 }}>
            {hasFilters ? "Try clearing the filters or changing the criteria." : "Schedule a DocMaster live session for your students."}
          </div>
          {!hasFilters && (
            <button onClick={() => { setEditItem(null); setShowSchedule(true); }} style={{
              padding: "10px 24px", borderRadius: 10, border: "none",
              background: "linear-gradient(135deg,#4f46e5,#06b6d4)",
              color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}>+ Schedule First Class</button>
          )}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 16 }}>
          {filteredClasses.map((item) => (
            <LiveClassCard
              key={item.id}
              item={item}
              isAdminOrTeacher
              onJoin={() => setJoinItem(item)}
              onEdit={(i) => { setEditItem(i); setShowSchedule(true); }}
              onDelete={setConfirmDel}
              onRefreshStatus={load}
            />
          ))}
        </div>
      )}


      {/* Modals */}
      {showSchedule && (
        <ScheduleModal
          onClose={() => { setShowSchedule(false); setEditItem(null); }}
          onSave={handleSave}
          schoolClasses={schoolClasses}
          editItem={editItem}
          schoolId={schoolId}
        />
      )}
      {joinItem && (
        <JoinModal liveClass={joinItem} onClose={() => setJoinItem(null)} />
      )}
      {confirmDel && (
        <ConfirmModal
          message={<>Delete <strong>{confirmDel.title}</strong>? This will also end the BBB meeting.</>}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════
   INVITE DRAWER
══════════════════════════════ */
export function InviteDrawer({ liveClass, onClose }) {
  const [invitations, setInvitations]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [emailInput, setEmailInput]     = useState("");
  const [sending, setSending]           = useState(false);
  const [students, setStudents]         = useState([]);
  const [studLoading, setStudLoading]   = useState(true);
  const [checked, setChecked]           = useState({});
  const [searchFilter, setSearchFilter] = useState("");
  const [dropOpen, setDropOpen]         = useState(false);
  const dropRef = React.useRef(null);
  const user = useSelector((s) => s.user?.user || s.auth?.user || {});

  const schoolId = user?.school_id || user?.admin_id || null;

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropOpen) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropOpen]);

  const load = useCallback(() => {
    setLoading(true);
    lmsGetInvitations(liveClass.id)
      .then((r) => setInvitations(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [liveClass.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setStudLoading(true);
    // fetch only students of this class if live class has class_id
    const params = {};
    if (schoolId) params.school_id = schoolId;
    if (liveClass.class_id) params.class_id = liveClass.class_id;
    lmsGetStudents(params)
      .then((r) => {
        const data = r.data?.data || r.data?.students || (Array.isArray(r.data) ? r.data : []);
        setStudents(data);
      })
      .catch(() => setStudents([]))
      .finally(() => setStudLoading(false));
  }, [schoolId, liveClass.class_id]);


  const handleSend = async () => {
    const emails = emailInput.split(/[\s,;]+/).filter(Boolean);
    if (!emails.length) return toast.error("Enter at least one email");
    setSending(true);
    try {
      await lmsSendInvitations(liveClass.id, { emails, invited_by: user?.id || null });
      toast.success("Invitations sent");
      setEmailInput("");
      setChecked({});
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to send");
    } finally { setSending(false); }
  };

  const handleRevoke = async (invId) => {
    try {
      await lmsRevokeInvitation(liveClass.id, invId);
      setInvitations((p) => p.map((i) => i.id === invId ? { ...i, status: "expired" } : i));
    } catch { toast.error("Failed to revoke"); }
  };

  const filteredStudents = students.filter((s) => {
    if (!searchFilter) return true;
    const q = searchFilter.toLowerCase();
    return (
      (s.name || s.student_name || "").toLowerCase().includes(q) ||
      (s.email || "").toLowerCase().includes(q) ||
      (s.class_name || s.class || "").toLowerCase().includes(q)
    );
  });

  const checkedCount = Object.values(checked).filter(Boolean).length;
  const selectedNames = students.filter((s) => checked[s.id]).map((s) => s.name || s.student_name || s.email).join(", ");
  const statusColor = { pending: "#f59e0b", accepted: "#16a34a", expired: "#9ca3af" };

  return createPortal(
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, width: 580, maxWidth: "95vw",
        boxShadow: "0 24px 60px rgba(0,0,0,0.18)", overflow: "hidden",
        maxHeight: "90vh", display: "flex", flexDirection: "column",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#f59e0b,#f97316)" }} />

        {/* Header */}
        <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Mail size={18} color="#b45309" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>Invite by Email</div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>{liveClass.title}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "#f1f5f9", borderRadius: 8, cursor: "pointer", padding: 6, color: "#6b7280", display: "flex" }}>
            <X size={15} />
          </button>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {/* Student custom dropdown */}
          <div style={{ padding: "16px 22px", borderBottom: "1px solid #f1f5f9" }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 }}>
              Select Students {checkedCount > 0 && <span style={{ color: "#f59e0b" }}>({checkedCount} selected)</span>}
            </label>

            {/* Trigger */}
            <div ref={dropRef} style={{ position: "relative" }}>
              <div
                onClick={() => setDropOpen((o) => !o)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 12px", borderRadius: 8,
                  border: `1.5px solid ${dropOpen ? "#f59e0b" : "#e5e7eb"}`,
                  background: "#fff", cursor: "pointer", minHeight: 38,
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 13, color: checkedCount ? "#111827" : "#9ca3af", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {checkedCount ? selectedNames : "Select students..."}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  {checkedCount > 0 && (
                    <span
                      onClick={(e) => { e.stopPropagation(); setChecked({}); setEmailInput(""); }}
                      style={{ color: "#9ca3af", cursor: "pointer", display: "flex", lineHeight: 1 }}
                      title="Clear"
                    >
                      <X size={14} />
                    </span>
                  )}
                  <span style={{ color: "#9ca3af", display: "flex", lineHeight: 1 }}>▾</span>
                </div>
              </div>

              {/* Dropdown panel */}
              {dropOpen && (
                <div style={{
                  position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 999,
                  background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 10,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden",
                }}>
                  {/* Search */}
                  <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
                    <input
                      autoFocus
                      value={searchFilter}
                      onChange={(e) => setSearchFilter(e.target.value)}
                      placeholder="Search students..."
                      style={{
                        width: "100%", padding: "6px 10px", borderRadius: 7,
                        border: "1.5px solid #e5e7eb", fontSize: 13, outline: "none",
                        color: "#374151", background: "#fff", boxSizing: "border-box",
                      }}
                    />
                  </div>

                  {/* Options */}
                  <div style={{ maxHeight: 220, overflowY: "auto" }}>
                    {studLoading ? (
                      <div style={{ textAlign: "center", padding: "16px 0", color: "#9ca3af" }}><Loader size={15} className="ms2-spin" /></div>
                    ) : filteredStudents.length === 0 ? (
                      <div style={{ padding: "12px 14px", fontSize: 13, color: "#9ca3af" }}>No students found</div>
                    ) : filteredStudents.map((stu) => {
                      const name     = stu.name || stu.student_name || stu.email || "Unknown";
                      const email    = stu.email || "";
                      const cls      = stu.class_name || stu.class || "";
                      const isChecked = !!checked[stu.id];
                      return (
                        <div
                          key={stu.id}
                          onClick={() => {
                            if (!email) return;
                            const next = { ...checked, [stu.id]: !isChecked };
                            setChecked(next);
                            const emails = students.filter((s) => next[s.id] && s.email).map((s) => s.email);
                            setEmailInput(emails.join(", "));
                          }}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "9px 14px",
                            background: isChecked ? "#eff6ff" : "#fff",
                            cursor: email ? "pointer" : "default",
                            borderBottom: "1px solid #f9fafb",
                          }}
                        >
                          {/* Checkbox square */}
                          <div style={{
                            width: 17, height: 17, borderRadius: 4, flexShrink: 0,
                            border: isChecked ? "none" : "2px solid #d1d5db",
                            background: isChecked ? "#2563eb" : "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            {isChecked && (
                              <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                <path d="M1 4L3.5 6.5L9 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: isChecked ? 700 : 500, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {name}
                            </div>
                            {cls && <div style={{ fontSize: 11, color: "#9ca3af" }}>{cls}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Email input + send */}
          <div style={{ padding: "16px 22px", borderBottom: "1px solid #f1f5f9" }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 }}>
              Email addresses <span style={{ fontWeight: 400, color: "#9ca3af" }}>(comma or space separated)</span>
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                style={{ ...inp({ flex: 1 }) }}
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="student1@school.com, student2@school.com"
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
              />
              <button onClick={handleSend} disabled={sending} style={{
                padding: "9px 18px", borderRadius: 9, border: "none",
                background: "linear-gradient(135deg,#f59e0b,#f97316)", color: "#fff",
                fontWeight: 700, fontSize: 13, cursor: sending ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
              }}>
                {sending ? <Loader size={13} className="ms2-spin" /> : <Mail size={13} />}
                Send
              </button>
            </div>
          </div>

          {/* Sent invitations */}
          <div style={{ padding: "14px 22px 20px" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 10 }}>Sent Invitations</div>
            {loading ? (
              <div style={{ textAlign: "center", padding: "24px 0", color: "#9ca3af" }}><Loader size={18} className="ms2-spin" /></div>
            ) : invitations.length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px 0", color: "#9ca3af", fontSize: 13 }}>No invitations sent yet</div>
            ) : invitations.map((inv) => (
              <div key={inv.id} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "9px 12px", borderRadius: 9, marginBottom: 6,
                background: "#f9fafb", border: "1px solid #f1f5f9",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Mail size={13} color="#9ca3af" />
                  <span style={{ fontSize: 13, color: "#111827" }}>{inv.email}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                    color: statusColor[inv.status] || "#9ca3af",
                    background: `${statusColor[inv.status]}18` || "#f3f4f6",
                  }}>
                    {inv.status}
                  </span>
                  {inv.status === "pending" && (
                    <button onClick={() => handleRevoke(inv.id)} title="Revoke" style={{
                      border: "none", background: "transparent", cursor: "pointer",
                      color: "#dc2626", display: "flex", padding: 2,
                    }}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ══════════════════════════════
   POLL & Q&A DRAWER
══════════════════════════════ */
export function PollQADrawer({ liveClass, isAdminOrTeacher, onClose }) {
  const [tab, setTab]                     = useState("poll"); // "poll" | "qa"
  const [activePoll, setActivePoll]       = useState(null);
  const [pollLoading, setPollLoading]     = useState(true);
  const [qaMessages, setQaMessages]       = useState([]);
  const [pendingQA, setPendingQA]         = useState([]);
  const [qaLoading, setQaLoading]         = useState(false);
  const [myVote, setMyVote]               = useState(null);
  const [voting, setVoting]               = useState(false);
  // Create poll form (moderator)
  const [newQuestion, setNewQuestion]     = useState("");
  const [newOptions, setNewOptions]       = useState(["", ""]);
  const [creating, setCreating]           = useState(false);
  // Q&A submit (student)
  const [qaInput, setQaInput]             = useState("");
  const [sending, setSending]             = useState(false);

  const user = useSelector((s) => s.user?.user || s.auth?.user || {});
  const userId = user?.id;
  const userName = user?.name || user?.full_name || "Anonymous";

  const loadPoll = useCallback(() => {
    setPollLoading(true);
    lmsGetActivePoll(liveClass.id)
      .then((r) => setActivePoll(r.data.data))
      .catch(() => setActivePoll(null))
      .finally(() => setPollLoading(false));
  }, [liveClass.id]);

  const loadQA = useCallback(() => {
    setQaLoading(true);
    const role = isAdminOrTeacher ? "moderator" : "attendee";
    lmsGetQA(liveClass.id, { role })
      .then((r) => setQaMessages(r.data.data || []))
      .catch(() => {})
      .finally(() => setQaLoading(false));
    if (isAdminOrTeacher) {
      lmsGetPendingQA(liveClass.id)
        .then((r) => setPendingQA(r.data.data || []))
        .catch(() => {});
    }
  }, [liveClass.id, isAdminOrTeacher]);

  useEffect(() => { loadPoll(); }, [loadPoll]);
  useEffect(() => { if (tab === "qa") loadQA(); }, [tab, loadQA]);

  // Poll: moderator creates
  const handleCreatePoll = async () => {
    const opts = newOptions.filter((o) => o.trim());
    if (!newQuestion.trim() || opts.length < 2) return toast.error("Question and at least 2 options required");
    setCreating(true);
    try {
      await lmsCreatePoll(liveClass.id, { question: newQuestion.trim(), options: opts, created_by: userId });
      setNewQuestion(""); setNewOptions(["", ""]);
      loadPoll();
      toast.success("Poll launched");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed");
    } finally { setCreating(false); }
  };

  // Poll: student votes
  const handleVote = async (pollId, optIdx) => {
    if (myVote !== null) return;
    setVoting(true);
    try {
      await lmsVotePoll(liveClass.id, pollId, { user_id: userId, option_idx: optIdx });
      setMyVote(optIdx);
      loadPoll();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Vote failed");
    } finally { setVoting(false); }
  };

  const handleClosePoll = async () => {
    try {
      await lmsClosePoll(liveClass.id, activePoll.id);
      setActivePoll(null);
      toast.success("Poll closed");
    } catch { toast.error("Failed to close poll"); }
  };

  // Q&A: student submits
  const handleSendQuestion = async () => {
    if (!qaInput.trim()) return;
    setSending(true);
    try {
      await lmsSubmitQuestion(liveClass.id, { user_id: userId, user_name: userName, message: qaInput.trim() });
      setQaInput("");
      toast.success("Question submitted for moderation");
    } catch { toast.error("Failed to submit"); }
    finally { setSending(false); }
  };

  const handleApprove = async (msgId) => {
    await lmsApproveQuestion(liveClass.id, msgId).catch(() => {});
    loadQA();
  };
  const handleAnswered = async (msgId) => {
    await lmsMarkAnswered(liveClass.id, msgId).catch(() => {});
    loadQA();
  };
  const handleDeleteQ = async (msgId) => {
    await lmsDeleteQAMessage(liveClass.id, msgId).catch(() => {});
    loadQA();
  };

  const tabStyle = (active) => ({
    flex: 1, padding: "9px 0", border: "none", background: "none", cursor: "pointer",
    fontSize: 13, fontWeight: active ? 700 : 500,
    color: active ? "#4f46e5" : "#6b7280",
    borderBottom: `2.5px solid ${active ? "#4f46e5" : "transparent"}`,
  });

  return createPortal(
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, width: 560, maxWidth: "95vw",
        maxHeight: "88vh", display: "flex", flexDirection: "column",
        boxShadow: "0 24px 60px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#4f46e5,#06b6d4)" }} />

        {/* Header */}
        <div style={{ padding: "16px 22px 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>Polls & Q&A</div>
          <button onClick={onClose} style={{ border: "none", background: "#f1f5f9", borderRadius: 8, cursor: "pointer", padding: 6, color: "#6b7280", display: "flex" }}>
            <X size={15} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid #f1f5f9", margin: "0 22px" }}>
          <button style={tabStyle(tab === "poll")} onClick={() => setTab("poll")}>
            <BarChart2 size={13} style={{ marginRight: 5, verticalAlign: "middle" }} />Poll
          </button>
          <button style={tabStyle(tab === "qa")} onClick={() => setTab("qa")}>
            <MessageSquare size={13} style={{ marginRight: 5, verticalAlign: "middle" }} />Q&A
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px 20px" }}>

          {/* ── POLL TAB ── */}
          {tab === "poll" && (
            <>
              {pollLoading ? (
                <div style={{ textAlign: "center", padding: 24, color: "#9ca3af" }}><Loader size={18} className="ms2-spin" /></div>
              ) : activePoll ? (
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 12 }}>{activePoll.question}</div>
                  {activePoll.tally.map((opt, idx) => {
                    const pct = activePoll.total_votes > 0 ? Math.round(opt.count / activePoll.total_votes * 100) : 0;
                    const isVoted = myVote === idx;
                    return (
                      <div key={idx} style={{ marginBottom: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ fontSize: 13, color: "#374151", fontWeight: isVoted ? 700 : 400 }}>{opt.label}</span>
                          <span style={{ fontSize: 12, color: "#6b7280" }}>{opt.count} ({pct}%)</span>
                        </div>
                        <div style={{ height: 8, borderRadius: 4, background: "#f1f5f9", overflow: "hidden" }}>
                          <div style={{ height: "100%", borderRadius: 4, background: isVoted ? "#4f46e5" : "#a5b4fc", width: `${pct}%`, transition: "width 0.4s" }} />
                        </div>
                        {!isAdminOrTeacher && myVote === null && (
                          <button onClick={() => handleVote(activePoll.id, idx)} disabled={voting} style={{
                            marginTop: 4, padding: "4px 12px", fontSize: 12, borderRadius: 6,
                            border: "1.5px solid #e0e7ff", background: "#eff6ff", color: "#4f46e5",
                            cursor: voting ? "not-allowed" : "pointer", fontWeight: 600,
                          }}>Vote</button>
                        )}
                      </div>
                    );
                  })}
                  {isAdminOrTeacher && (
                    <button onClick={handleClosePoll} style={{
                      marginTop: 12, padding: "8px 18px", borderRadius: 9, border: "none",
                      background: "#fee2e2", color: "#dc2626", fontWeight: 700, fontSize: 12, cursor: "pointer",
                    }}>Close Poll</button>
                  )}
                </div>
              ) : (
                isAdminOrTeacher ? (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 8 }}>Launch a new poll</div>
                    <input style={inp({ marginBottom: 10 })} placeholder="Poll question..."
                      value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)} />
                    {newOptions.map((opt, idx) => (
                      <div key={idx} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                        <input style={inp({ flex: 1 })} placeholder={`Option ${idx + 1}`}
                          value={opt} onChange={(e) => setNewOptions((p) => p.map((o, i) => i === idx ? e.target.value : o))} />
                        {newOptions.length > 2 && (
                          <button onClick={() => setNewOptions((p) => p.filter((_, i) => i !== idx))}
                            style={{ border: "none", background: "#fee2e2", color: "#dc2626", borderRadius: 7, padding: "0 10px", cursor: "pointer" }}>
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <button onClick={() => setNewOptions((p) => [...p, ""])} style={{
                        padding: "7px 14px", borderRadius: 8, border: "1.5px solid #e0e7ff",
                        background: "#eff6ff", color: "#4f46e5", fontSize: 12, fontWeight: 600, cursor: "pointer",
                      }}>+ Option</button>
                      <button onClick={handleCreatePoll} disabled={creating} style={{
                        padding: "7px 18px", borderRadius: 8, border: "none",
                        background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff",
                        fontSize: 12, fontWeight: 700, cursor: creating ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", gap: 5,
                      }}>
                        {creating ? <Loader size={12} className="ms2-spin" /> : <BarChart2 size={12} />} Launch
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: 24, color: "#9ca3af", fontSize: 13 }}>No active poll right now</div>
                )
              )}
            </>
          )}

          {/* ── Q&A TAB ── */}
          {tab === "qa" && (
            <>
              {/* Pending moderation (moderator only) */}
              {isAdminOrTeacher && pendingQA.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#b45309", marginBottom: 8 }}>
                    Pending Approval ({pendingQA.length})
                  </div>
                  {pendingQA.map((q) => (
                    <div key={q.id} style={{ padding: "10px 12px", borderRadius: 9, background: "#fffbeb", border: "1px solid #fde68a", marginBottom: 6 }}>
                      <div style={{ fontSize: 13, color: "#111827", marginBottom: 6 }}>{q.message}</div>
                      <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 6 }}>{q.user_name}</div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => handleApprove(q.id)} style={{
                          padding: "4px 10px", fontSize: 11, borderRadius: 6, border: "none",
                          background: "#dcfce7", color: "#16a34a", fontWeight: 700, cursor: "pointer",
                        }}>Approve</button>
                        <button onClick={() => handleDeleteQ(q.id)} style={{
                          padding: "4px 10px", fontSize: 11, borderRadius: 6, border: "none",
                          background: "#fee2e2", color: "#dc2626", fontWeight: 700, cursor: "pointer",
                        }}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Approved messages */}
              {qaLoading ? (
                <div style={{ textAlign: "center", padding: 24, color: "#9ca3af" }}><Loader size={18} className="ms2-spin" /></div>
              ) : qaMessages.length === 0 ? (
                <div style={{ textAlign: "center", padding: 24, color: "#9ca3af", fontSize: 13 }}>No questions yet</div>
              ) : qaMessages.map((q) => (
                <div key={q.id} style={{
                  padding: "10px 12px", borderRadius: 9, marginBottom: 8,
                  background: q.is_answered ? "#f0fdf4" : "#f9fafb",
                  border: `1px solid ${q.is_answered ? "#bbf7d0" : "#f1f5f9"}`,
                }}>
                  <div style={{ fontSize: 13, color: "#111827" }}>{q.message}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>{q.user_name}</div>
                  {isAdminOrTeacher && !q.is_answered && (
                    <button onClick={() => handleAnswered(q.id)} style={{
                      marginTop: 6, padding: "3px 10px", fontSize: 11, borderRadius: 6, border: "none",
                      background: "#d1fae5", color: "#065f46", fontWeight: 700, cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 4,
                    }}>
                      <CheckCheck size={11} /> Mark Answered
                    </button>
                  )}
                  {q.is_answered && <span style={{ fontSize: 11, color: "#16a34a", fontWeight: 700, marginTop: 4, display: "block" }}>Answered</span>}
                </div>
              ))}

              {/* Submit question (students) */}
              {!isAdminOrTeacher && (
                <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                  <input style={inp({ flex: 1 })} placeholder="Ask a question..."
                    value={qaInput} onChange={(e) => setQaInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSendQuestion()} />
                  <button onClick={handleSendQuestion} disabled={sending} style={{
                    padding: "9px 14px", borderRadius: 9, border: "none",
                    background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff",
                    cursor: sending ? "not-allowed" : "pointer", display: "flex", alignItems: "center",
                  }}>
                    {sending ? <Loader size={13} className="ms2-spin" /> : <Send size={13} />}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
